/**
 * Checks a filter value's shape against its operator, before it leaves the browser.
 * `kind` is the column or field kind; leave it out when only the operator is known.
 */
export function validOperatorValue(operator: string, value: unknown, kind?: string): boolean {
  if (operator === 'empty') return typeof value === 'boolean'
  if (operator === 'in' || operator === 'notIn') {
    return Array.isArray(value) && value.every((entry) => typeof entry === 'string')
  }
  if (operator === 'between') {
    return kind === 'number' ? numberRange(value)
      : kind === undefined ? numberRange(value) || objectWith(value, 'from', 'to')
        : objectWith(value, 'from', 'to')
  }
  if (kind === 'number' || operator === 'atLeast' || operator === 'atMost') {
    return validNumber(value)
  }
  if (operator === 'relative') {
    return typeof value === 'object' && value !== null
      && 'amount' in value && typeof value.amount === 'number' && value.amount > 0
      && 'unit' in value && (value.unit === 'hours' || value.unit === 'days')
  }
  if (operator === 'age') {
    if (typeof value !== 'object' || value === null) return false
    const { min, max } = value as { min?: unknown; max?: unknown }
    return (min !== undefined || max !== undefined) && [min, max].every((period) => period === undefined || validPeriod(period))
  }
  if (kind === undefined && operator === 'equals') {
    return typeof value === 'string' || validNumber(value)
  }
  return typeof value === 'string'
}

function validPeriod(value: unknown) {
  return typeof value === 'object' && value !== null
    && 'amount' in value && typeof value.amount === 'number' && Number.isInteger(value.amount) && value.amount >= 0
    && 'unit' in value && (value.unit === 'hours' || value.unit === 'days')
}

function validNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value)
    && value >= -2147483648 && value <= 2147483647
}

export function numberRange(value: unknown): value is { from: number; to: number } {
  return typeof value === 'object' && value !== null
    && 'from' in value && validNumber(value.from)
    && 'to' in value && validNumber(value.to)
    && value.from <= value.to
}

export function objectWith(value: unknown, first: string, second: string): boolean {
  return typeof value === 'object' && value !== null
    && first in value && typeof value[first as keyof typeof value] === 'string'
    && second in value && typeof value[second as keyof typeof value] === 'string'
}
